import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Scale, Subtract, Total } from "@pdfking/math"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction } from "@pdfking/renderer-api"

import { PALETTE } from "#constants"
import type { ContentLabels } from "#internal"

import { PlotNotebookBackground } from "../layout/PlotNotebookBackground"
import { CreateMeasure } from "../text/CreateMeasure"
import { PlainText } from "../text/PlainText"
import { NotebookBackground } from "../types/NotebookBackground"

const MEASURE = CreateMeasure()

// The lines over the first letter's, the heading's badge across the first two
const HEAD = 4

/** An agenda's useful contacts, as the shop's templates have them: `frame`
 * ruled down to its foot, HEAD lines, then a letter's of the alphabet and a
 * free one under it for each, the heading on a badge of the primary on its
 * top right; each letter on its line. Its words `labels`. */
export const DrawAgendaContacts = ({
  frame,
  labels,
}: {
  frame: SpatialRectangle<Distance>
  labels: ContentLabels
}): PageShape[] => {
  const letters = labels.ALPHABET().split(" ")
  const lines = HEAD + 2 * letters.length
  // the last line half a row over the frame's foot
  const row = Scale(frame.height, 1 / (lines + 1.5))
  const top = Total(frame.y, frame.height)
  const line = (i: number) => Subtract(top, Scale(row, i))
  const heading = labels.CONTACTS()
  const font = Scale(row, 1.2)
  const pad = Scale(font, 0.3)
  const badge_width = Total(MEASURE(heading, font), pad, pad)
  const badge = {
    height: Scale(row, 2),
    width: badge_width,
    x: Subtract(Total(frame.x, frame.width), badge_width),
    y: line(2),
  }
  const letter = Scale(row, 0.7)
  const indent = Scale(row, 0.3)
  return [
    ...PlotNotebookBackground({ background: NotebookBackground.LINES, box: frame, pitch: row }),
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: badge },
    PlainText(
      heading,
      badge.x,
      badge.width,
      Total(badge.y, Scale(Subtract(badge.height, font), 0.5)),
      font,
      PALETTE.paper,
      HorizontalAlign.CENTER
    ),
    ...letters.map((name, i) =>
      PlainText(
        name,
        Total(frame.x, indent),
        Subtract(frame.width, indent),
        Total(line(HEAD + 1 + 2 * i), Scale(row, 0.15)),
        letter,
        PALETTE.primary
      )
    ),
  ]
}
